import { ShieldAlert } from 'lucide-react';
import { useMcfSignataire, type McfSignataire } from '../../api/queries/mcf';
import { useTranslation } from '../../lib/billing-i18n';
import { invoiceIssueStatusOf } from '../../lib/invoice-issue-status';

/**
 * #328 — tells every screen that the établissement must certify its
 * invoices (`mcfRequis`) while its signer does not certify them
 * (`certifie` false). Both come from GET /api/mcf/signataire; the sentence
 * follows the device class it returns and, where one applies, names the
 * status the invoices carry with the invoice list's own label
 * (`invoiceIssueStatusOf`). Nothing shows otherwise, nor for a user who may
 * not read invoices.
 */

const LEAD = 'la certification des factures est obligatoire pour cet établissement';

const EVALUATION_WITHOUT_STATUS =
  `${LEAD}, mais le dispositif configuré pour les signer ne certifie pas (dispositif d'évaluation) : ` +
  'ses factures ne peuvent pas être certifiées. Contactez votre installateur agréé.';

const NONE_SENTENCE =
  `${LEAD}, mais aucun dispositif ne peut signer ses factures : aucun agent MCF actif ne le sert. ` +
  "Reliez l'agent MCF du poste où le MCF est branché, ou contactez votre installateur agréé.";

const FROZEN_SENTENCE =
  "l'attestation de conformité de la version de ce logiciel de facturation est retirée : aucun dispositif ne " +
  'peut certifier les factures de cet établissement. Mettez à jour le logiciel vers une version homologuée.';

const NO_CERTIFIED_SIGNER = `${LEAD}, mais aucun MCF certifié ne signe ses factures. Contactez votre installateur agréé.`;

/** The sentence for a signer the API says does not certify. */
function signerSentence(signataire: McfSignataire, label: (modalite: string | null) => string | null): string {
  const status = label(signataire.modalite);
  switch (signataire.dispositif) {
    case 'EVALUATION':
      return status === null
        ? EVALUATION_WITHOUT_STATUS
        : `${LEAD}, mais le dispositif qui signe ses factures ne certifie pas : elles portent le statut « ${status} ». ` +
            'Contactez votre installateur agréé.';
    case 'NONE':
      return NONE_SENTENCE;
    case 'FROZEN':
      return FROZEN_SENTENCE;
    case 'CERTIFYING':
      return status === null
        ? NO_CERTIFIED_SIGNER
        : `${LEAD} ; la dernière facture que l'agent MCF du site a signée pour lui porte le statut « ${status} ». ` +
            "Vérifiez que l'agent MCF est relié au MCF, ou contactez votre installateur agréé.";
    case null:
      return NO_CERTIFIED_SIGNER;
  }
}

export function McfSignataireBanner() {
  const { data } = useMcfSignataire();
  const { t } = useTranslation();
  if (!data || !data.mcfRequis || data.certifie) return null;

  // The badge's label of a modality that does not certify; null for any other.
  const nonCertifiedLabel = (modalite: string | null): string | null => {
    if (modalite === null) return null;
    const status = invoiceIssueStatusOf({ fiscalModality: modalite });
    return status.kind === 'nonCertified' ? t(status.labelKey) : null;
  };

  return (
    <div
      role="alert"
      data-testid="mcf-signataire-banner"
      className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
    >
      <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <p className="min-w-0">
        <span className="font-semibold">Certification des factures</span> —{' '}
        {signerSentence(data, nonCertifiedLabel)}
      </p>
    </div>
  );
}

export default McfSignataireBanner;
